/* eslint-disable react/no-array-index-key */
import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';

import { EDIT_STATUS_MODIFIED, EDIT_STATUS_NEW } from 'app/App/const';
import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import DiscardButton from 'app/DiscardButton/container';
import PublishPage from 'app/PublishPage/components/PublishPage';
import sharedStyles from 'app/shared.css';
import { MODAL_MESSAGES } from 'lib/const';
import { isBlank } from 'lib/string';
import { isPageValid } from 'lib/validations';
import { PUBLISHED } from 'lib/versions';
import {
  AlertButton,
  DuplicateComponentNamesMessage,
  PrimaryButton,
  SecondaryButton
} from 'ui/Components';
import { Link } from 'utils/store/thirdPartyConnectToCanvasStore';

import styles from './PageButtonsList.css';

/**
 * render Component for page Buttons
 */
export default class PageButtonsList extends Component {
  static propTypes = {
    apiResponse: propTypes.shape(),
    clearPageApiResponse: propTypes.func.isRequired,
    editStatus: propTypes.string.isRequired,
    failedValidations: propTypes.object.isRequired,
    isCompassPage: propTypes.bool,
    isPageValid: propTypes.func,
    isReadonly: propTypes.bool,
    pagePayload: propTypes.object.isRequired,
    publishPage: propTypes.func.isRequired,
    routeMapping: propTypes.array,
    savePage: propTypes.func.isRequired,
    saveAndPublishPage: propTypes.func.isRequired,
    setFailedValidations: propTypes.func.isRequired,
    unboundVisitComponent: propTypes.func.isRequired,
    unboundVisitPage: propTypes.func.isRequired,
    unpublishPage: propTypes.func.isRequired,
    visitPage: propTypes.func.isRequired,
    contractMap: propTypes.object.isRequired
  };

  static defaultProps = {
    isPageValid,
    apiResponse: undefined,
    isCompassPage: null,
    isReadonly: true,
    routeMapping: []
  };

  constructor(props) {
    super(props);

    this.state = {
      confirmationModal: { isOpen: false },
      isPublishPageModalOpen: false,
      publishPageProps: {
        onPublishPage: this.onPublishPage,
        publishDate: props.pagePayload.StartDate,
        isTestContent: props.pagePayload.IsTestContent
      }
    };

    this.prepareFailedValidationModalBody = this.prepareFailedValidationModalBody.bind(this);
    this.handleUnpublishPage = this.handleUnpublishPage.bind(this);
    this.hideConfirmationModal = this.hideConfirmationModal.bind(this);
    this.isValidToPublish = this.isValidToPublish.bind(this);
    this.onPublishPage = this.onPublishPage.bind(this);
    this.onSaveAndPublishPage = this.onSaveAndPublishPage.bind(this);
    this.publishPageToggle = this.publishPageToggle.bind(this);
    this.renderUnpublishButton = this.renderUnpublishButton.bind(this);
    this.showPublishPageModal = this.showPublishPageModal.bind(this);
    this.showSaveAndPublishModal = this.showSaveAndPublishModal.bind(this);
    this.showUnpublishPageConfirmation = this.showUnpublishPageConfirmation.bind(this);
    this.showValidationModal = this.showValidationModal.bind(this);
    this.validateAndSavePage = this.validateAndSavePage.bind(this);
    this.renderConfirmationModal = this.renderConfirmationModal.bind(this);
    this.retrieveFinalValidationMessage = this.retrieveFinalValidationMessage.bind(this);
  }

  /**
   * hides yes/no confirmation modal and unpublishes the page
   */
  handleUnpublishPage() {
    const { pagePayload, unpublishPage } = this.props;
    this.hideConfirmationModal();
    unpublishPage(pagePayload);
  }

  /**
   * toggles the publish page modal and calls the onPublish callback
   * @param {object} page - page to be published
   * @param {boolean} publishNow - publish true/false
   * @param {string} publishDate - UTC date time of publish
   * @param {boolean} isTestContent - whether content is published for testing purposes only
   */
  onPublishPage(page, publishNow, publishDate, isTestContent) {
    const { publishPage } = this.props;
    this.publishPageToggle();
    publishPage(page, publishNow, publishDate, isTestContent);
  }

  /**
   * toggles the save and publish page modal and
   * calls the save and publish page callback
   * @param {object} page - page to be published
   * @param {boolean} publishNow - publish true/false
   * @param {string} publishDate - UTC date time of publish
   */
  onSaveAndPublishPage(page, publishNow, publishDate) {
    const { saveAndPublishPage } = this.props;
    this.publishPageToggle();
    if (this.isValidToPublish()) {
      saveAndPublishPage(page, publishNow, publishDate);
    }
  }

  /**
   * shows a confirmation modal to override existing publish for the same route and time.
   * @param {Array} conflictingRoutes - array of conflicting routes.
   * @returns {object} state to show conflicting routes modal
   */
  showPublishConflictingRoutes(conflictingRoutes) {
    const { clearPageApiResponse, unboundVisitPage } = this.props;

    const pagesList = conflictingRoutes.map(cr =>
      (cr.pageIds || []).map(pageId => (
        <div>
          <Link target="_blank" to={unboundVisitPage(pageId, PUBLISHED)}>
            {cr.route}
          </Link>
        </div>
      ))
    );

    const children = (
      <Fragment>
        Route(s) &nbsp;
        <br />
        {pagesList}
        already has content scheduled to go live at this time. &nbsp; To proceed please click
        the route(s) above and ensure the &nbsp;
        <strong>Remove Content</strong>
        &nbsp; box is checked. Then try scheduling your publish again.
      </Fragment>
    );

    return {
      isOpen: true,
      yesButtonLabel: 'Schedule Publish',
      onYesClick: () => {
        clearPageApiResponse();
        this.hideConfirmationModal();
        this.showPublishPageModal();
      },
      noButtonLabel: 'Cancel Publish',
      onNoClick: () => {
        clearPageApiResponse();
        this.hideConfirmationModal();
      },
      children
    };
  }

  /**
   * identifies final failed validation message
   * @param {object} params - method params
   * @param {string[]|string} params.message - validation message
   * @param {string} params.componentName - component name
   * @returns {string|object} returns either message string for non-duplicate component name failed validations, or jsx object for duplicate component name failed validation
   */
  retrieveFinalValidationMessage({ message, componentName = '' }) {
    if (Array.isArray(message)) {
      // failed validation from multiple malformed links from LiveTextEditor come here in the form of an array
      return `${MODAL_MESSAGES.LIVE_TEXT_EDITOR_FAILED_MESSAGE} Component Name: ${componentName}`;
    } else if (typeof message === 'string' && componentName) {
      // failed malformed link validations come here as a string type with a component name
      return `${message} Component Name: ${componentName}`;
    }

    // failed message for all other validations
    return message;
  }

  /**
   * shows a modal to show failed validations
   * @param {object} failedValidations - Object containing failed validations
   * @returns {Component} return failed validation messages
   */
  prepareFailedValidationModalBody(failedValidations) {
    const children = (
      <ul className="p-4">
        {Object.keys(failedValidations).map((Id, index1) =>
          Object.keys(failedValidations[Id]).map(key => (
            <li key={`${Id}-${index1}-${key}`}>
              <span className={sharedStyles.validationMessage}>
                {this.retrieveFinalValidationMessage(failedValidations[Id][key])}
              </span>
            </li>
          ))
        )}
      </ul>
    );

    return {
      isOpen: true,
      children
    };
  }

  /**
   * shows modal with failed validation messages
   * @param {Array} failedValidations - array containing failed validation messages
   */
  showValidationModal(failedValidations) {
    const modalBody = this.prepareFailedValidationModalBody(failedValidations);
    this.setState({
      confirmationModal: {
        ...modalBody,
        onOkClick: this.hideConfirmationModal
      }
    });
  }

  /**
   * validates page before showing publish modal
   * @returns {boolean} true if valid for publish else false
   */
  isValidToPublish() {
    const { isCompassPage, pagePayload, routeMapping, setFailedValidations } = this.props;
    if (isCompassPage) {
      return true;
    }
    if (
      routeMapping &&
      routeMapping.length > 0 &&
      routeMapping.findIndex(route => !isBlank(route.RouteURL)) > -1
    ) {
      return true;
    }
    const failedValidations = {
      [pagePayload.Id]: {
        Publish: { message: 'At least one route is required to publish a WCM page!' }
      }
    };
    setFailedValidations(failedValidations);
    this.showValidationModal(failedValidations);
    return false;
  }

  /**
   * hides the confirmation modal
   */
  hideConfirmationModal() {
    this.setState({ confirmationModal: { isOpen: false } });
  }

  /**
   * shows yes/no confirmation modal for unpublish page
   */
  showUnpublishPageConfirmation() {
    this.setState({
      confirmationModal: {
        isOpen: true,
        onYesClick: this.handleUnpublishPage,
        onNoClick: this.hideConfirmationModal,
        children:
          'Are you sure you want to unpublish this page? Once unpublished this content will no longer appear on the site.'
      }
    });
  }

  /**
   * toggles the publish page modal
   * if page is valid to publish
   */
  showPublishPageModal() {
    if (this.isValidToPublish()) {
      this.setState({
        publishPageProps: {
          onPublishPage: this.onPublishPage,
          publishDate: this.props.pagePayload.StartDate,
          isTestContent: this.props.pagePayload.IsTestContent
        }
      });
      this.publishPageToggle();
    }
  }

  /**
   * toggles the publish page modal
   */
  publishPageToggle() {
    this.setState(ps => ({ isPublishPageModalOpen: !ps.isPublishPageModalOpen }));
  }

  /**
   * toggles the save and publish page modal
   */
  showSaveAndPublishModal() {
    this.setState({
      publishPageProps: {
        headerText: 'Save & Publish Page',
        message:
          'This page is either currently published or scheduled to be published! Changes to this page have to be published or scheduled after save. If you do not wish to publish or schedule a publish at this time, please create a copy of the page.',
        onPublishPage: this.onSaveAndPublishPage,
        publishDate: this.props.pagePayload.StartDate,
        publishNowButtonText: 'Save & Publish Live Now',
        scheduleButtonText: 'Save & Schedule Publish',
        isTestContent: this.props.pagePayload.IsTestContent
      }
    });
    this.publishPageToggle();
  }

  /**
   * validates the page and makes save page call
   */
  validateAndSavePage() {
    const {
      isPageValid,
      pagePayload,
      savePage,
      setFailedValidations,
      contractMap
    } = this.props;
    const failedValidations = isPageValid(pagePayload, contractMap);
    setFailedValidations(failedValidations);
    const hasFailedValidations = Object.keys(failedValidations).some(
      Id => Object.keys(failedValidations[Id]).length > 0
    );
    if (hasFailedValidations) {
      this.showValidationModal(failedValidations);
    } else if (pagePayload.Published) {
      this.showSaveAndPublishModal();
    } else {
      savePage();
    }
  }

  /**
   * renders discard button
   * @returns {Component} returns discard button
   */
  renderDiscardButton() {
    const { editStatus, isReadonly, pagePayload, visitPage } = this.props;
    return (
      <div className={styles.buttonWrapper}>
        <DiscardButton
          data-test-id="pageButtonsList-discardPage"
          disabled={
            isReadonly ||
            (editStatus !== EDIT_STATUS_MODIFIED && editStatus !== EDIT_STATUS_NEW)
          }
          id={pagePayload.Id}
          name={pagePayload.PageName}
          parentFolderId={pagePayload.ParentFolderId}
          type="page"
          version={pagePayload.Version}
          visit={visitPage}
        />
      </div>
    );
  }

  /**
   * renders a ConfirmationModal with ok button
   * renders a unpublish component button
   * @returns {React.Component} button with click handler
   */
  renderUnpublishButton() {
    const { editStatus, isReadonly } = this.props;
    return (
      <div className={styles.buttonWrapper}>
        <AlertButton
          className={styles.unpublishButton}
          onClick={this.showUnpublishPageConfirmation}
          data-test-id="pageButtonsList-unpublishPage"
          disabled={
            isReadonly || editStatus === EDIT_STATUS_MODIFIED || editStatus === EDIT_STATUS_NEW
          }
        >
          Unpublish
        </AlertButton>
      </div>
    );
  }

  /**
   * renders publish button
   * @returns {Component} returns publish button
   */
  renderPublishButton() {
    const { editStatus, isCompassPage, isReadonly, pagePayload } = this.props;
    const { isPublishPageModalOpen, publishPageProps } = this.state;
    return (
      <div className={styles.buttonWrapper}>
        <PrimaryButton
          onClick={this.showPublishPageModal}
          data-test-id="pageButtonsList-publishPage"
          disabled={
            isReadonly || editStatus === EDIT_STATUS_MODIFIED || editStatus === EDIT_STATUS_NEW
          }
        >
          Publish Page
        </PrimaryButton>
        <PublishPage
          isOpen={isPublishPageModalOpen}
          modalToggle={this.publishPageToggle}
          pagePayload={pagePayload}
          isCompassPage={isCompassPage}
          {...publishPageProps}
        />
      </div>
    );
  }

  /**
   * renders a unpublish component button
   * @returns {React.Component} button with click handler
   */
  renderConfirmationModal() {
    const {
      apiResponse: { conflictingRoutes, duplicateNamedComponents } = {},
      clearPageApiResponse,
      unboundVisitComponent
    } = this.props;
    let { confirmationModal } = this.state;
    if (conflictingRoutes) {
      confirmationModal = this.showPublishConflictingRoutes(conflictingRoutes);
    } else if (duplicateNamedComponents) {
      confirmationModal = this.prepareFailedValidationModalBody({
        allComponents: {
          ComponentNames: {
            message: (
              <DuplicateComponentNamesMessage
                duplicateNamedComponents={duplicateNamedComponents}
                visitComponent={unboundVisitComponent}
              />
            )
          }
        }
      });
      confirmationModal.onOkClick = clearPageApiResponse;
    }
    const { children, ...restProps } = confirmationModal;
    return (
      <ConfirmationModal {...restProps} data-test-id="pageButtonsList-confirmationModal">
        {children}
      </ConfirmationModal>
    );
  }

  /**
   * renders save page button
   * @returns {Component} returns save button
   */
  renderSaveButton() {
    const { editStatus, isReadonly } = this.props;

    return (
      <div className={styles.buttonWrapper}>
        <SecondaryButton
          data-test-id="pageButtonsList-savePage"
          disabled={
            isReadonly ||
            (editStatus !== EDIT_STATUS_MODIFIED && editStatus !== EDIT_STATUS_NEW)
          }
          onClick={this.validateAndSavePage}
        >
          Save Page
        </SecondaryButton>
      </div>
    );
  }

  render() {
    return (
      <div>
        <div className={styles.buttons}>
          {this.renderSaveButton()}
          {this.renderDiscardButton()}
          {this.renderPublishButton()}
          {this.renderUnpublishButton()}
        </div>
        {this.renderConfirmationModal()}
      </div>
    );
  }
}
